import type { ActivityEntry } from "@/services/activity.service";
import type { TaskChange } from "@/services/task-history.service";

const FIELD_LABELS: Record<string, string> = {
	assigned_to: "assignee",
	status: "status",
	priority: "priority",
	due_date: "due date",
	estimated_time: "estimate",
	sprint_id: "sprint",
	task_type_id: "type",
	parent_task_id: "parent task",
	tags: "tags",
	title: "title",
	description: "description",
	developer_notes: "notes",
};

function formatValue(
	field: string,
	value: unknown,
	name?: string | null,
): string {
	if (name) return name;
	if (value === null || value === undefined || value === "") return "none";
	if (field === "due_date") {
		const d = new Date(String(value));
		return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
	}
	if (field === "estimated_time") return `${value}h`;
	if (field === "tags" && Array.isArray(value))
		return value.length ? value.join(", ") : "none";
	if (typeof value === "string")
		return value.charAt(0).toUpperCase() + value.slice(1);
	return String(value);
}

function describeOne(c: TaskChange): string {
	const label = FIELD_LABELS[c.field] ?? c.field.replace(/_/g, " ");
	if (c.edited) {
		if (c.was_empty && !c.is_now_empty) return `added ${label}`;
		if (!c.was_empty && c.is_now_empty) return `removed ${label}`;
		return `updated ${label}`;
	}
	if (c.field === "title" && c.to)
		return `changed title to "${c.to}"`;
	const to = formatValue(c.field, c.to, c.to_name);
	if (c.field === "assigned_to" && to === "none")
		return "removed assignee";
	return `updated ${label} to ${to}`;
}

function getChanges(entry: ActivityEntry): TaskChange[] {
	const md = entry.metadata as Record<string, unknown>;
	return (md.changes as TaskChange[] | undefined) ?? [];
}

function getChangedFields(entry: ActivityEntry): string[] {
	const md = entry.metadata as Record<string, unknown>;
	return (md.changed_fields as string[] | undefined) ?? [];
}

export function describeChanges(entry: ActivityEntry): string[] {
	const md = entry.metadata as Record<string, unknown>;
	switch (entry.action) {
		case "task.created":
			return ["created this task"];
		case "task.deleted":
			return ["deleted this task"];
		case "task.moved": {
			const from = md.from_column_name as string | undefined;
			const to = md.to_column_name as string | undefined;
			if (from && to) return [`moved from ${from} to ${to}`];
			return ["moved this task"];
		}
		case "task.checklist_updated": {
			const change = md.change as string | undefined;
			const itemTitle = md.item_title as string | undefined;
			const label = itemTitle ? `"${itemTitle}"` : "checklist item";
			const template = md.template as string | undefined;
			if (change === "template_applied")
				return [`applied checklist template "${template ?? ""}"`];
			if (change === "added") return [`added checklist item ${label}`];
			if (change === "completed") return [`completed checklist item ${label}`];
			if (change === "reopened") return [`reopened checklist item ${label}`];
			if (change === "deleted") return [`deleted checklist item ${label}`];
			if (change === "renamed") return [`renamed checklist item to ${label}`];
			return ["updated checklist"];
		}
		case "task.attachment_added": {
			const fileName = md.file_name as string | undefined;
			return [`attached ${fileName ?? "a file"}`];
		}
		case "task.attachment_deleted": {
			const fileName = md.file_name as string | undefined;
			return [`removed attachment${fileName ? ` ${fileName}` : ""}`];
		}
		case "comment.created":
			return ["added a comment"];
		case "comment.updated":
			return ["edited a comment"];
		case "comment.deleted":
			return ["deleted a comment"];
		default:
			break;
	}

	const changes = getChanges(entry);
	if (changes.length) return changes.map(describeOne);

	// Backward-compatible fallback for rows logged before `changes` existed.
	if (entry.action === "task.status_changed") {
		return [`updated status to ${formatValue("status", md.to)}`];
	}
	const fields = getChangedFields(entry);
	if (fields.length)
		return [
			`updated ${fields
				.map((f) => FIELD_LABELS[f] ?? f.replace(/_/g, " "))
				.join(", ")}`,
		];
	return ["updated this task"];
}
